import React, { useState, useEffect } from 'react';
import { useParams } from "react-router";
import {
    Container,
    Typography,
    IconButton,
    Tooltip,
    Box,
    Button,
    CircularProgress,
    Grid2,
    FormControl,
    TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ForwardIcon from '@mui/icons-material/Forward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function MappingFields() {
    const [item, setItem] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fields, setFields] = useState([]);
    const [exampleInputData, setExampleInputData] = useState('{}');
    const [exampleOutputData, setExampleOutputData] = useState('{}');
    const [errorExampleInputData, setErrorExampleInputData] = useState(null);
    let params = useParams();

    /* >>>> API calls */
    async function getMappingFields() {
        try {
            setLoading(true);
            const response = await fetch(`/api/mapping/${params.id}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setItem(data);
                setFields(data.mappings || []);
            } else {
                setItem({});
            }
        } catch (error) {
            console.error('Error fetching mappings:', error);
            setItem({});
        } finally {
            setLoading(false);
        }
    }

    async function updateMappingField(field) {
        try {
            setLoading(true);
            const response = await fetch(`/api/mapping/${params.id}/mapping/${field.id}`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(field)
            });
            if (response.ok) {
                const data = await response.json();
                console.log('Mappings updated successfully:', data);
            } else {
                console.error('Failed to update mappings');
            }
        } catch (error) {
            console.error('Error updating mappings:', error);
        } finally {
            setLoading(false);
        }
    }

    async function addMappingField() {
        try {
            setLoading(true);
            const newField = { source: '', target: '' };
            const response = await fetch(`/api/mapping/${params.id}/mapping`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newField)
            });
            if (response.ok) {
                const data = await response.json();
                console.log('New mapping field added:', data);
                setFields(prevFields => [...prevFields, data]);
            } else {
                console.error('Failed to add mapping field');
            }
        } catch (error) {
            console.error('Error adding mapping field:', error);
        } finally {
            setLoading(false);
        }
    }

    async function deleteMappingField(field) {
        try {
            setLoading(true);
            const response = await fetch(`/api/mapping/${params.id}/mapping/${field.id}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (response.ok) {
                setFields(prevFields => prevFields.filter(f => f.id !== field.id));
            } else {
                console.error('Failed to delete mapping field');
            }
        } catch (error) {
            console.error('Error deleting mapping field:', error);
        } finally {
            setLoading(false);
        }
    }

    async function mapData() {
        try {
            const response = await fetch(`/api/mapping/${params.id}/mapdata`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: exampleInputData
            });
            if (response.ok) {
                const data = await response.json();
                console.log('Data mapped successfully:', data);
                setExampleOutputData(JSON.stringify(data, null, 2));
                setErrorExampleInputData(null);
            } else {
                console.error('Failed to map data');
            }
        } catch (error) {
            console.error('Error mapping data:', error);
        }    }
    /* <<<< API calls */

    /* >>>> effects */
    useEffect(() => {
        getMappingFields();
    }, [params.id]);
    /* <<<< effects */

    const handleFieldChange = (index, fieldType, value) => {
        setFields(prevFields => {
            const newFields = [...prevFields];
            if (fieldType === 'source') {
                newFields[index].source = value;
            } else if (fieldType === 'target') {
                newFields[index].target = value;
            }
            return newFields;
        });
    }

    const handleAddField = () => {
        addMappingField();
    }

    const handleUpdateField = (field) => {
        updateMappingField(field);
    }

    const handleExampleInputDataChange = (event) => {
        const newData = event.target.value;
        setExampleInputData(newData);
        try {
            JSON.parse(newData);
            // setExampleOutputData(JSON.stringify(JSON.parse(newData), null, 2));
            setErrorExampleInputData(null);
            // mapData();
        } catch (error) {
            setErrorExampleInputData('Invalid JSON format');
        }
    }

    const handleMapData = () => {
        if (errorExampleInputData == null) {
            mapData();
        }
    }

    return (
        <Container>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">{item.name ?? ''}</Typography>
            </Box>
            <Box>
                <Grid2 container columns={14} spacing={1}>
                    <Grid2 size={6}>
                        <Typography variant="subtitle1" color="textSecondary">Source Field</Typography>
                    </Grid2>
                    <Grid2 size={1}>
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <ForwardIcon />
                        </Box>
                    </Grid2>
                    <Grid2 size={6}>
                        <Typography variant="subtitle1" color="textSecondary">Target Field</Typography>
                    </Grid2>
                    <Grid2 size={1}>
                    </Grid2>
                </Grid2>
            </Box>
            <hr />
            <Box display="flex" height="90vh" width="100%" flexDirection={"column"} justifyContent="space-between">
                <Box height="60%" style={{ overflowY: 'auto' }}>
                    <Box>
                        {loading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box>
                                {item.length === 0 ? (
                                    <Typography variant="body1" color="textSecondary">
                                        No mapping fields available.
                                    </Typography>
                                ) : (
                                    fields.map((field) => (
                                        <Grid2 container columns={15} key={field.id ?? field.source} spacing={0}>
                                            <Grid2 size={6}>
                                                <FormControl fullWidth margin="normal">
                                                    <TextField
                                                        size="small"
                                                        id={`field-label-${field.source}-${field.target}`}
                                                        name={`field-label-${field.source}-${field.target}`}
                                                        type="text"
                                                        value={field.source ?? ''}
                                                        onChange={(e) => handleFieldChange(fields.indexOf(field), 'source', e.target.value)}
                                                    />
                                                </FormControl>
                                            </Grid2>
                                            <Grid2 size={1}>
                                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                                    <ForwardIcon />
                                                </Box>
                                            </Grid2>
                                            <Grid2 size={6}>
                                                <FormControl fullWidth margin="normal">
                                                    <TextField
                                                        size="small"
                                                        id={`field-target-${field.source}-${field.target}`}
                                                        name={`field-target-${field.source}-${field.target}`}
                                                        type="text"
                                                        value={field.target ?? ''}
                                                        onChange={(e) => handleFieldChange(fields.indexOf(field), 'target', e.target.value)}
                                                    />
                                                </FormControl>
                                            </Grid2>
                                            <Grid2 size={1}>
                                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                                    <Tooltip title="Accept Changes">
                                                        <IconButton color="primary" onClick={() => handleUpdateField(field)}>
                                                            <CheckCircleIcon color="success" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Grid2>
                                            <Grid2 size={1}>
                                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                                    <Tooltip title="Create New Group">
                                                        <IconButton color="primary" onClick={() => deleteMappingField(field)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Grid2>
                                        </Grid2>
                                    ))
                                )}
                                <Button variant="outlined" onClick={handleAddField}>Add New Field</Button>
                            </Box>
                        )}
                    </Box>
                </Box>
                <Box height={"50%"} width="100%">
                    <Grid2 container spacing={2} columns={16}>
                        <Grid2 size={8}>
                            <FormControl fullWidth margin="normal">
                                <TextField
                                    error={errorExampleInputData != null}
                                    helperText={errorExampleInputData}
                                    label="Input data example (JSON)"
                                    id={`input-data-example-${params.id}`}
                                    name={`input-data-example-${params.id}`}
                                    type="text"
                                    multiline
                                    value={exampleInputData}
                                    minRows={8}
                                    maxRows={8}
                                    onChange={handleExampleInputDataChange}
                                />
                            </FormControl>
                        </Grid2>
                        <Grid2 size={8}>
                            <FormControl fullWidth margin="normal">
                                <TextField
                                    label="output data"
                                    id={`output-data-example-${params.id}`}
                                    name={`output-data-example-${params.id}`}
                                    type="text"
                                    multiline
                                    value={exampleOutputData}
                                    minRows={8}
                                    maxRows={8}
                                    readOnly
                                />
                            </FormControl>
                        </Grid2>
                    </Grid2>
                    <Button variant="contained" color="primary" onClick={handleMapData} disabled={errorExampleInputData != null || loading}>
                        Map
                    </Button>
                </Box>
            </Box>
        </Container>
    );
}
